import React, { useState, useEffect, useCallback } from 'react';
import { financeAPI } from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { DollarSign, ChevronDown, ChevronUp, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  currency: 'USD',
  client_charge: 0,
  photo_editing_cost: 0,
  video_editing_cost: 0,
  album_cost: 0,
  extra_expenses: 0,
  crew_cost_override: null,
};

const FinanceRow = ({ label, k, cur, value, onChange }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <label className="text-sm text-muted-foreground">{label}</label>
    <div className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground">{cur === 'INR' ? '₹' : '$'}</span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={onChange}
        className="w-28 px-2 py-1 text-sm text-right border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground bg-background focus:border-gold focus:outline-none"
        data-testid={`finance-${k}`}
      />
    </div>
  </div>
);

const ProjectFinancePanel = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [crewAuto, setCrewAuto] = useState({ USD: 0, INR: 0 });
  const [saving, setSaving] = useState(false);
  const [overrideOn, setOverrideOn] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await financeAPI.getProject(projectId);
      const d = r.data;
      setForm({
        currency: d.currency || 'USD',
        client_charge: d.client_charge || 0,
        photo_editing_cost: d.photo_editing_cost || 0,
        video_editing_cost: d.video_editing_cost || 0,
        album_cost: d.album_cost || 0,
        extra_expenses: d.extra_expenses || 0,
        crew_cost_override: d.crew_cost_override,
      });
      setOverrideOn(d.crew_cost_override !== null && d.crew_cost_override !== undefined);
      setCrewAuto({ USD: d.crew_cost_auto_usd || 0, INR: d.crew_cost_auto_inr || 0 });
    } catch (e) {
      // silent
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const num = (v) => (parseFloat(v) || 0);
  const cur = form.currency;
  const crewCost = overrideOn ? num(form.crew_cost_override) : (crewAuto[cur] || 0);
  const totalCost = crewCost + num(form.photo_editing_cost) + num(form.video_editing_cost) + num(form.album_cost) + num(form.extra_expenses);
  const profit = num(form.client_charge) - totalCost;
  const margin = num(form.client_charge) > 0 ? (profit / num(form.client_charge) * 100) : 0;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await financeAPI.updateProject(projectId, {
        currency: form.currency,
        client_charge: num(form.client_charge),
        photo_editing_cost: num(form.photo_editing_cost),
        video_editing_cost: num(form.video_editing_cost),
        album_cost: num(form.album_cost),
        extra_expenses: num(form.extra_expenses),
        crew_cost_override: overrideOn ? num(form.crew_cost_override) : null,
      });
      toast.success('Finances saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 sm:p-5" data-testid="project-finance-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between"
        data-testid="finance-panel-toggle"
      >
        <span className="flex items-center gap-2 text-lg font-serif">
          <DollarSign size={18} className="text-gold" /> Finances (P&L)
        </span>
        <span className="flex items-center gap-3">
          {!open && (
            <span className={`text-sm font-medium ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatCurrency(profit, cur)} profit
            </span>
          )}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {open && (
        <div className="mt-4">
          {/* Currency toggle */}
          <div className="flex gap-1 mb-4 border border-border w-fit rounded overflow-hidden">
            {['USD', 'INR'].map(c => (
              <button
                key={c}
                onClick={() => set('currency', c)}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${cur === c ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
                data-testid={`finance-currency-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Revenue</p>
              <FinanceRow label="Client Charge" k="client_charge" cur={cur} value={form.client_charge || ''} onChange={(e) => set('client_charge', e.target.value)} />

              <p className="text-xs uppercase tracking-wide text-muted-foreground mt-4 mb-1">Costs</p>
              <div className="flex items-center justify-between gap-3 py-1.5">
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  Crew Cost
                  <button
                    type="button"
                    onClick={() => setOverrideOn(v => !v)}
                    className="text-[10px] uppercase tracking-wide underline text-gold"
                    data-testid="finance-crew-override-toggle"
                  >
                    {overrideOn ? 'auto' : 'override'}
                  </button>
                </label>
                {overrideOn ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">{cur === 'INR' ? '₹' : '$'}</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={form.crew_cost_override || ''}
                      onChange={(e) => set('crew_cost_override', e.target.value)}
                      className="w-28 px-2 py-1 text-sm text-right border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground bg-background focus:border-gold focus:outline-none"
                      data-testid="finance-crew-cost-override"
                    />
                  </div>
                ) : (
                  <span className="text-sm" data-testid="finance-crew-cost-auto">{formatCurrency(crewAuto[cur] || 0, cur)}</span>
                )}
              </div>
              <FinanceRow label="Photo Editing" k="photo_editing_cost" cur={cur} value={form.photo_editing_cost || ''} onChange={(e) => set('photo_editing_cost', e.target.value)} />
              <FinanceRow label="Video Editing" k="video_editing_cost" cur={cur} value={form.video_editing_cost || ''} onChange={(e) => set('video_editing_cost', e.target.value)} />
              <FinanceRow label="Album Cost" k="album_cost" cur={cur} value={form.album_cost || ''} onChange={(e) => set('album_cost', e.target.value)} />
              <FinanceRow label="Extra Expenses" k="extra_expenses" cur={cur} value={form.extra_expenses || ''} onChange={(e) => set('extra_expenses', e.target.value)} />
            </div>

            <div className="mt-4 md:mt-0">
              <div className="bg-muted/40 p-4 rounded space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Revenue</span>
                  <span data-testid="finance-total-revenue">{formatCurrency(num(form.client_charge), cur)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Costs</span>
                  <span className="text-red-600 dark:text-red-400" data-testid="finance-total-cost">− {formatCurrency(totalCost, cur)}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between items-center">
                  <span className="text-sm font-medium">Profit</span>
                  <span className={`text-xl font-serif ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="finance-profit">
                    {formatCurrency(profit, cur)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Margin</span>
                  <span className={profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} data-testid="finance-margin">
                    {margin.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90 disabled:opacity-50 text-sm"
                  data-testid="finance-save-btn"
                >
                  <Save size={14} /> {saving ? 'Saving...' : 'Save Finances'}
                </button>
                <button
                  onClick={load}
                  className="flex items-center gap-2 border border-border px-4 py-2 hover:bg-accent text-sm"
                  data-testid="finance-reset-btn"
                >
                  <RotateCcw size={14} /> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFinancePanel;
